// Offline watcher + functional emergency fallback.
//
// When the device goes offline, any authenticated screen that mounts this
// hook (via __root) attempts a router-first navigation to /offline-control.
// We deliberately DO NOT call window.location.href / window.location.replace
// while offline: the WKWebView is still on the remote origin, so a hard
// navigation resolves against https://roava.lovable.app/offline-control and
// fails with NSURLErrorDomain -1009 in a loop. Instead, EmergencyOfflineFallback
// renders as a fully functional offline control surface — same cached-trip,
// tracking, status, and start/stop wiring as the real /offline-control route —
// so the user is never blocked even if router navigation can't repaint.
//
// OTA-safe: pure JS. Uses @capacitor/network on native and window
// online/offline on web.

import { createElement, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { addConnectionListener, getConnectionType, type ConnectionType } from "@/lib/native/network";
import { OfflineControl } from "@/routes/offline-control";

const SAFE_PATHS = ["/offline-control", "/login"];
const OFFLINE_CONTROL_PATH = "/offline-control";
const AUTO_FORCE_GUARD_MS = 1500;
const ROUTER_RETRY_MS = 1200;

function isSafePath(path: string): boolean {
  return SAFE_PATHS.some((p) => path === p || path.startsWith(p + "/"));
}

function navigatorOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function offlineControlRenderedNow(): boolean {
  return (
    typeof document !== "undefined" &&
    document.querySelector('[data-roava-route="offline-control"]') !== null
  );
}

// Module-level navigator slot so forceToOfflineControl() can call the router
// from outside React (button onClick, setTimeout, etc.).
type RouterNavFn = (opts: { to: string; replace?: boolean }) => unknown;
let routerNavSlot: RouterNavFn | null = null;
let lastForceAt = 0;

export interface ForceOptions {
  userInitiated?: boolean;
}

/**
 * Router-first navigation attempt to /offline-control.
 *
 * IMPORTANT: We intentionally never call window.location.href or
 * window.location.replace here. While offline, the WKWebView is still on the
 * remote origin and a hard navigation triggers NSURLErrorDomain -1009 in a
 * loop. The EmergencyOfflineFallback below is fully functional on its own,
 * so if the router can't repaint we simply leave the user on the fallback.
 */
export function forceToOfflineControl(reason: string, opts: ForceOptions = {}): void {
  const userInitiated = opts.userInitiated === true;
  const now = Date.now();
  if (!userInitiated && now - lastForceAt < AUTO_FORCE_GUARD_MS) return;
  lastForceAt = now;

  console.warn(`[offline-control] ${reason}`);

  try {
    routerNavSlot?.({ to: OFFLINE_CONTROL_PATH, replace: true });
  } catch {
    /* router may be unmounted; the emergency fallback remains usable */
  }
}

export interface UseOfflineRedirectOptions {
  /** If true (default), redirect to /offline-control when offline is observed. */
  redirect?: boolean;
}

export function useOfflineRedirect(opts: UseOfflineRedirectOptions = {}): { isOffline: boolean } {
  const redirect = opts.redirect !== false;
  const nav = useNavigate();
  const loc = useLocation();
  const [isOffline, setIsOffline] = useState<boolean>(navigatorOffline());
  const navRef = useRef(nav);
  const pathnameRef = useRef(loc.pathname);

  useEffect(() => {
    navRef.current = nav;
  }, [nav]);

  useEffect(() => {
    pathnameRef.current = loc.pathname;
  }, [loc.pathname]);

  useEffect(() => {
    const fn: RouterNavFn = (o) => navRef.current(o as never);
    routerNavSlot = fn;
    return () => {
      if (routerNavSlot === fn) routerNavSlot = null;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const apply = (offline: boolean) => {
      if (!alive) return;
      setIsOffline(offline);
      if (!offline) return;
      if (!redirect) return;
      if (isSafePath(pathnameRef.current)) return;
      forceToOfflineControl("network offline route fallback");
    };

    void getConnectionType().then((t: ConnectionType) => {
      apply(t === "none" || navigatorOffline());
    });

    const sub = addConnectionListener((t) => apply(t === "none"));
    const onOnline = () => apply(false);
    const onOffline = () => apply(true);
    if (typeof window !== "undefined") {
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
    }

    return () => {
      alive = false;
      sub.remove();
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
      }
    };
  }, [redirect]);

  return { isOffline };
}

/**
 * EmergencyOfflineFallback
 *
 * Renders an immediate, fully functional offline control surface whenever the
 * device goes offline and the real /offline-control route hasn't painted yet.
 * Mirrors the cached-trip / tracking / status wiring used by the real route so
 * the user is never blocked by WebView navigation failures.
 *
 * Hidden automatically as soon as the real /offline-control route paints
 * (detected via the `data-roava-route="offline-control"` marker).
 */
export function EmergencyOfflineFallback({ active }: { active: boolean }) {
  const loc = useLocation();
  const realRouteRendered = loc.pathname === OFFLINE_CONTROL_PATH;
  const loggedRef = useRef(false);

  // Log once per activation while the fallback is actually visible.
  useEffect(() => {
    if (!active || realRouteRendered || loggedRef.current) return;
    loggedRef.current = true;
    console.warn("[offline-control] emergency inline fallback rendered");
  }, [active, realRouteRendered]);
  useEffect(() => {
    if (!active) loggedRef.current = false;
  }, [active]);

  // Background router attempt only — never a hard navigation. If it succeeds,
  // loc.pathname flips to /offline-control and this overlay dismisses cleanly.
  useEffect(() => {
    if (!active || realRouteRendered) return;
    forceToOfflineControl("emergency fallback router attempt");
    const t = window.setTimeout(() => {
      if (typeof document !== "undefined" &&
          !document.querySelector('[data-roava-route="offline-control"]')) {
        forceToOfflineControl("emergency fallback router retry");
      }
    }, ROUTER_RETRY_MS);
    return () => window.clearTimeout(t);
  }, [active, realRouteRendered]);

  if (!active || realRouteRendered) return null;

  return createElement(
    "div",
    {
      className:
        "fixed inset-0 z-[9999] overflow-y-auto bg-background safe-top safe-bottom",
    },
    createElement(OfflineControl),
  );
}

